<template>
  <v-row>
    <v-col cols="12">
      <v-card class="gst-card" flat>
        <!-- HEADER BAND -->
        <div class="gst-header pl-base-bg">
          <div class="gst-header-icon">
            <v-icon color="#fff" size="22">mdi-file-percent-outline</v-icon>
          </div>
          <div>
            <div class="gst-title">GST Reports</div>
            <div class="gst-subtitle">GSTR-1-ready B2B, B2C and HSN summaries</div>
          </div>
        </div>

        <div class="pa-6">
          <v-alert type="info" outlined dense border="left" class="gst-disclaimer">
            GSTR-1 (outward supplies, including Credit Notes — CDNR/CDNUR)
            and an ITC summary (eligible input tax credit on ordinary
            domestic purchases, net of Debit Notes) are covered below.
            Reverse charge, imports, and ISD credit are not — and this is
            not a full GSTR-3B computation. Always verify totals with your
            accountant before filing.
          </v-alert>

          <v-tabs v-model="activeTab" class="gst-tabs">
            <v-tab>GSTR-1</v-tab>
            <v-tab>ITC Summary</v-tab>
            <v-tab>GSTR-3B</v-tab>
          </v-tabs>

          <!-- FILTERS -->
          <div class="gst-filter-bar">
            <v-row dense align="center">
              <v-col cols="12" md="3">
                <v-select
                  v-model="filters.type"
                  :items="periodTypes"
                  item-text="text"
                  item-value="value"
                  label="Period Type"
                  outlined
                  dense
                  hide-details
                />
              </v-col>

              <!-- MONTH -->
              <v-col cols="12" md="3" v-if="filters.type === 'month'">
                <v-menu
                  v-model="monthMenu"
                  :close-on-content-click="false"
                  offset-y
                  max-width="290"
                  min-width="290"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="formattedMonth"
                      label="Month"
                      readonly
                      outlined
                      dense
                      hide-details
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>

                  <v-date-picker
                    v-model="filters.month"
                    type="month"
                    @input="monthMenu = false"
                  />
                </v-menu>
              </v-col>

              <!-- QUARTER (Indian FY: Apr-Jun / Jul-Sep / Oct-Dec / Jan-Mar) -->
              <template v-if="filters.type === 'quarter'">
                <v-col cols="6" md="2">
                  <v-select
                    v-model="filters.quarter"
                    :items="quarterOptions"
                    item-text="text"
                    item-value="value"
                    label="Quarter"
                    outlined
                    dense
                    hide-details
                  />
                </v-col>

                <v-col cols="6" md="2">
                  <v-text-field
                    v-model="filters.fyStartYear"
                    type="number"
                    label="FY Start Year"
                    outlined
                    dense
                    hide-details
                  />
                </v-col>
              </template>

              <!-- APPLY + EXPORTS -->
              <v-col cols="12" md="6" class="gst-actions">
                <v-btn class="gst-apply-btn" @click="loadSummary" :loading="loading">
                  Apply
                </v-btn>

                <v-btn
                  outlined
                  color="#d93025"
                  class="ml-2"
                  :disabled="activeTab === 0 ? !summary : !itcSummary"
                  @click="activeTab === 0 ? downloadPDF() : downloadItcPDF()"
                >
                  <v-icon left size="16" color="#d93025">mdi-file-pdf-box</v-icon>
                  PDF
                </v-btn>

                <v-btn
                  outlined
                  color="#1d6f42"
                  class="ml-2"
                  :disabled="activeTab === 0 ? !summary : !itcSummary"
                  @click="activeTab === 0 ? downloadExcel() : downloadItcExcel()"
                >
                  <v-icon left size="16" color="#1d6f42">mdi-file-excel-box</v-icon>
                  Excel
                </v-btn>

                <v-btn
                  v-if="activeTab === 0"
                  outlined
                  color="#0a5a90"
                  class="ml-2"
                  :loading="downloadingInvoices"
                  :disabled="downloadingInvoices"
                  @click="downloadInvoicesZip"
                >
                  <v-icon left size="16" color="#0a5a90">mdi-folder-zip</v-icon>
                  Download Invoices
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <v-tabs-items v-model="activeTab" class="gst-tab-content">
          <v-tab-item>

          <!-- OUTWARD TAX LIABILITY (6.6) -->
          <v-row dense class="mb-6" v-if="summary">
            <v-col cols="6" md="3">
              <v-card class="gst-kpi-card gst-kpi-card--value pa-4" flat>
                <div class="gst-kpi-label">Taxable Value</div>
                <div class="gst-kpi-value">{{ formatINR(summary.outwardTaxLiability.taxableValue) }}</div>
              </v-card>
            </v-col>
            <v-col cols="6" md="3">
              <v-card class="gst-kpi-card gst-kpi-card--tax pa-4" flat>
                <div class="gst-kpi-label">CGST + SGST</div>
                <div class="gst-kpi-value">
                  {{ formatINR(summary.outwardTaxLiability.cgst + summary.outwardTaxLiability.sgst) }}
                </div>
              </v-card>
            </v-col>
            <v-col cols="6" md="3">
              <v-card class="gst-kpi-card gst-kpi-card--tax pa-4" flat>
                <div class="gst-kpi-label">IGST</div>
                <div class="gst-kpi-value">{{ formatINR(summary.outwardTaxLiability.igst) }}</div>
              </v-card>
            </v-col>
            <v-col cols="6" md="3">
              <v-card class="gst-kpi-card gst-kpi-card--total pa-4" flat>
                <div class="gst-kpi-label">Total Tax Liability</div>
                <div class="gst-kpi-value">{{ formatINR(summary.outwardTaxLiability.total) }}</div>
              </v-card>
            </v-col>
          </v-row>

          <template v-if="summary">
            <!-- DOCUMENT SUMMARY (6.4) -->
            <div class="gst-section-title">Document Summary</div>
            <v-row dense class="mb-6">
              <v-col cols="6" md="3">
                <div class="gst-doc-stat">
                  <div class="gst-doc-label">Invoice Range</div>
                  <div class="gst-doc-value">
                    {{ summary.documentSummary.firstInvoiceNumber || "-" }} – {{ summary.documentSummary.lastInvoiceNumber || "-" }}
                  </div>
                </div>
              </v-col>
              <v-col cols="6" md="3">
                <div class="gst-doc-stat">
                  <div class="gst-doc-label">Total Issued</div>
                  <div class="gst-doc-value">{{ summary.documentSummary.totalIssued }}</div>
                </div>
              </v-col>
              <v-col cols="6" md="3">
                <div class="gst-doc-stat">
                  <div class="gst-doc-label">Cancelled</div>
                  <div class="gst-doc-value">{{ summary.documentSummary.totalCancelled }}</div>
                </div>
              </v-col>
              <v-col cols="6" md="3">
                <div class="gst-doc-stat">
                  <div class="gst-doc-label">Net</div>
                  <div class="gst-doc-value">{{ summary.documentSummary.net }}</div>
                </div>
              </v-col>
            </v-row>

            <!-- B2B (6.1) + B2C LARGE (6.2) -->
            <v-row dense class="mb-2">
              <v-col cols="12" md="6">
                <app-ag-grid
                  title="B2B Invoices"
                  :columnDefs="b2bColumns"
                  :rowData="summary.b2b"
                  :newBtn="false"
                  :refreshBtn="false"
                  :mobileCard="invoiceMobileCard"
                  height="height: 320px"
                />
              </v-col>

              <v-col cols="12" md="6">
                <app-ag-grid
                  title="B2C Large Invoices"
                  :columnDefs="b2cLargeColumns"
                  :rowData="summary.b2cLarge"
                  :newBtn="false"
                  :refreshBtn="false"
                  :mobileCard="invoiceMobileCard"
                  height="height: 320px"
                />
              </v-col>
            </v-row>

            <!-- B2C SMALL SUMMARY (6.3) + HSN SUMMARY (6.5) -->
            <v-row dense>
              <v-col cols="12" md="6">
                <app-ag-grid
                  title="B2C Small Summary"
                  :columnDefs="b2cSmallColumns"
                  :rowData="summary.b2cSmall"
                  :newBtn="false"
                  :refreshBtn="false"
                  :mobileCard="stateRateMobileCard"
                  height="height: 320px"
                />
              </v-col>

              <v-col cols="12" md="6">
                <app-ag-grid
                  title="HSN-wise Summary"
                  :columnDefs="hsnColumns"
                  :rowData="summary.hsnSummary"
                  :newBtn="false"
                  :refreshBtn="false"
                  :mobileCard="hsnMobileCard"
                  height="height: 320px"
                />
              </v-col>
            </v-row>

            <!-- CDNR (6.7a) + CDNUR (6.7b) -->
            <div class="gst-section-title mt-4">Credit / Debit Notes</div>
            <v-row dense>
              <v-col cols="12" md="6">
                <app-ag-grid
                  title="Credit Notes (Registered) — CDNR"
                  :columnDefs="cdnrColumns"
                  :rowData="summary.cdnr"
                  :newBtn="false"
                  :refreshBtn="false"
                  :mobileCard="cdnrMobileCard"
                  height="height: 280px"
                />
              </v-col>

              <v-col cols="12" md="6">
                <app-ag-grid
                  title="Credit Notes (Unregistered) — CDNUR"
                  :columnDefs="cdnurColumns"
                  :rowData="summary.cdnur"
                  :newBtn="false"
                  :refreshBtn="false"
                  :mobileCard="cdnurMobileCard"
                  height="height: 280px"
                />
              </v-col>
            </v-row>
          </template>

          <div v-else class="gst-empty-state">
            Select a period and click Apply to generate the report.
          </div>

          </v-tab-item>

          <v-tab-item>

          <!-- ITC SUMMARY -->
          <v-row dense class="mb-6" v-if="itcSummary">
            <v-col cols="6" md="3">
              <v-card class="gst-kpi-card gst-kpi-card--value pa-4" flat>
                <div class="gst-kpi-label">ITC Eligible (CGST+SGST)</div>
                <div class="gst-kpi-value">
                  {{ formatINR(itcSummary.itcEligible.cgst + itcSummary.itcEligible.sgst) }}
                </div>
              </v-card>
            </v-col>
            <v-col cols="6" md="3">
              <v-card class="gst-kpi-card gst-kpi-card--tax pa-4" flat>
                <div class="gst-kpi-label">ITC Eligible (IGST)</div>
                <div class="gst-kpi-value">{{ formatINR(itcSummary.itcEligible.igst) }}</div>
              </v-card>
            </v-col>
            <v-col cols="6" md="3">
              <v-card class="gst-kpi-card gst-kpi-card--total pa-4" flat>
                <div class="gst-kpi-label">Total ITC Eligible</div>
                <div class="gst-kpi-value">{{ formatINR(itcSummary.itcEligible.total) }}</div>
              </v-card>
            </v-col>
            <v-col cols="6" md="3">
              <v-card class="gst-kpi-card gst-kpi-card--tax pa-4" flat>
                <div class="gst-kpi-label">Total ITC Blocked</div>
                <div class="gst-kpi-value">{{ formatINR(itcSummary.itcBlocked.total) }}</div>
              </v-card>
            </v-col>
          </v-row>

          <v-row dense class="mb-6" v-if="itcSummary">
            <v-col cols="6" md="3">
              <v-card class="gst-kpi-card gst-kpi-card--tax pa-4" flat>
                <div class="gst-kpi-label">ITC Reversed (Debit Notes)</div>
                <div class="gst-kpi-value">{{ formatINR(itcSummary.itcReversed.total) }}</div>
              </v-card>
            </v-col>
            <v-col cols="6" md="3">
              <v-card class="gst-kpi-card gst-kpi-card--total pa-4" flat>
                <div class="gst-kpi-label">Net ITC Eligible</div>
                <div class="gst-kpi-value">{{ formatINR(itcSummary.netItcEligible.total) }}</div>
              </v-card>
            </v-col>
          </v-row>

          <template v-if="itcSummary">
            <div class="gst-section-title">HSN-wise ITC Summary</div>
            <v-row dense>
              <v-col cols="12">
                <app-ag-grid
                  title="HSN-wise ITC Summary"
                  :columnDefs="itcHsnColumns"
                  :rowData="itcSummary.hsnSummary"
                  :newBtn="false"
                  :refreshBtn="false"
                  :mobileCard="itcHsnMobileCard"
                  height="height: 400px"
                />
              </v-col>
            </v-row>
          </template>

          <div v-else class="gst-empty-state">
            Select a period and click Apply to generate the report.
          </div>

          </v-tab-item>

          <v-tab-item>

          <!-- GSTR-3B -->
          <v-alert type="warning" outlined dense border="left" class="gst-disclaimer">
            Net payable here is computed by netting CGST/SGST/IGST
            independently — it does <strong>not</strong> apply the
            statutory IGST-then-CGST-then-SGST credit-utilization order
            GSTR-3B legally requires. Treat this as directional, and
            always verify the actual filing figure with your accountant.
            <span v-if="gstr3b && gstr3b.itcSource === 'RECONCILED_2B'">
              ITC below is reconciled against imported GSTR-2B data for
              this period.
            </span>
            <span v-else>
              ITC below is taken from your books only — import GSTR-2A/2B
              for this period (Reconciliation page) to see it reconciled
              against vendor filings instead.
            </span>
          </v-alert>

          <template v-if="gstr3b">
            <v-row dense class="mb-6">
              <v-col cols="6" md="4">
                <v-card class="gst-kpi-card gst-kpi-card--tax pa-4" flat>
                  <div class="gst-kpi-label">Output Tax Liability</div>
                  <div class="gst-kpi-value">{{ formatINR(gstr3b.outputTax.total) }}</div>
                </v-card>
              </v-col>
              <v-col cols="6" md="4">
                <v-card class="gst-kpi-card gst-kpi-card--value pa-4" flat>
                  <div class="gst-kpi-label">
                    ITC ({{ gstr3b.itcSource === "RECONCILED_2B" ? "Reconciled" : "Books" }})
                  </div>
                  <div class="gst-kpi-value">
                    {{ formatINR(gstr3b.itcSource === "RECONCILED_2B" ? gstr3b.reconciledItc.total : gstr3b.booksItc.total) }}
                  </div>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card class="gst-kpi-card gst-kpi-card--total pa-4" flat>
                  <div class="gst-kpi-label">Net Payable (per books ITC)</div>
                  <div class="gst-kpi-value">{{ formatINR(gstr3b.netPayable.total) }}</div>
                </v-card>
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="12" md="6">
                <div class="gst-section-title">Output Tax Liability</div>
                <v-simple-table class="gst-table">
                  <tbody>
                    <tr><td>Taxable Value</td><td class="text-right">{{ formatINR(gstr3b.outputTax.taxableValue) }}</td></tr>
                    <tr><td>CGST</td><td class="text-right">{{ formatINR(gstr3b.outputTax.cgst) }}</td></tr>
                    <tr><td>SGST</td><td class="text-right">{{ formatINR(gstr3b.outputTax.sgst) }}</td></tr>
                    <tr><td>IGST</td><td class="text-right">{{ formatINR(gstr3b.outputTax.igst) }}</td></tr>
                    <tr class="gst-total-row"><td>Total</td><td class="text-right">{{ formatINR(gstr3b.outputTax.total) }}</td></tr>
                  </tbody>
                </v-simple-table>
              </v-col>

              <v-col cols="12" md="6">
                <div class="gst-section-title">ITC Eligible (Books)</div>
                <v-simple-table class="gst-table mb-4">
                  <tbody>
                    <tr><td>CGST</td><td class="text-right">{{ formatINR(gstr3b.booksItc.cgst) }}</td></tr>
                    <tr><td>SGST</td><td class="text-right">{{ formatINR(gstr3b.booksItc.sgst) }}</td></tr>
                    <tr><td>IGST</td><td class="text-right">{{ formatINR(gstr3b.booksItc.igst) }}</td></tr>
                    <tr class="gst-total-row"><td>Total</td><td class="text-right">{{ formatINR(gstr3b.booksItc.total) }}</td></tr>
                  </tbody>
                </v-simple-table>

                <template v-if="gstr3b.reconciledItc">
                  <div class="gst-section-title">ITC Reconciled Against GSTR-2B</div>
                  <v-simple-table class="gst-table">
                    <tbody>
                      <tr class="gst-total-row"><td>Total (matched)</td><td class="text-right">{{ formatINR(gstr3b.reconciledItc.total) }}</td></tr>
                    </tbody>
                  </v-simple-table>
                </template>
              </v-col>
            </v-row>

            <div class="gst-section-title mt-4">Net Payable</div>
            <v-simple-table class="gst-table">
              <tbody>
                <tr><td>CGST</td><td class="text-right">{{ formatINR(gstr3b.netPayable.cgst) }}</td></tr>
                <tr><td>SGST</td><td class="text-right">{{ formatINR(gstr3b.netPayable.sgst) }}</td></tr>
                <tr><td>IGST</td><td class="text-right">{{ formatINR(gstr3b.netPayable.igst) }}</td></tr>
                <tr class="gst-total-row"><td>Total</td><td class="text-right">{{ formatINR(gstr3b.netPayable.total) }}</td></tr>
              </tbody>
            </v-simple-table>
          </template>

          <div v-else class="gst-empty-state">
            Select a period and click Apply to generate the GSTR-3B summary.
          </div>

          </v-tab-item>
          </v-tabs-items>
        </div>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import AppAgGrid from "@/components/common/ag-grid.vue";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { downloadMultiSheetWorkbook } from "@/utils/xlsx-export";
import { triggerBlobDownload } from "@/utils/download-blob";

const FY_QUARTERS = {
  Q1: { startMonth: 3, endMonth: 5, label: "Q1 (Apr–Jun)" }, // 0-indexed months
  Q2: { startMonth: 6, endMonth: 8, label: "Q2 (Jul–Sep)" },
  Q3: { startMonth: 9, endMonth: 11, label: "Q3 (Oct–Dec)" },
  Q4: { startMonth: 0, endMonth: 2, label: "Q4 (Jan–Mar)" }, // falls in fyStartYear + 1
};

export default {
  name: "GstReportsPage",
  components: { AppAgGrid },

  data() {
    return {
      loading: false,
      downloadingInvoices: false,
      monthMenu: false,
      activeTab: 0,

      filters: {
        type: "month",
        month: "",
        quarter: "Q1",
        fyStartYear: this.defaultFyStartYear(),
      },

      periodTypes: [
        { text: "Month", value: "month" },
        { text: "Quarter", value: "quarter" },
      ],

      quarterOptions: Object.entries(FY_QUARTERS).map(([value, q]) => ({
        value,
        text: q.label,
      })),

      b2bColumns: [
        { headerName: "GSTIN", field: "customer.gstin", minWidth: 160 },
        { headerName: "Customer", field: "customer.name", minWidth: 150 },
        { headerName: "Invoice #", field: "invoiceNumber", minWidth: 110 },
        { headerName: "Date", field: "invoiceDate", valueFormatter: (p) => this.formatDate(p.value), minWidth: 110 },
        { headerName: "Place of Supply", field: "placeOfSupply", minWidth: 150 },
        { headerName: "Taxable Value", field: "subtotal", valueFormatter: (p) => this.formatINR(p.value), minWidth: 140 },
        { headerName: "CGST", field: "cgst", valueFormatter: (p) => this.formatINR(p.value), minWidth: 110 },
        { headerName: "SGST", field: "sgst", valueFormatter: (p) => this.formatINR(p.value), minWidth: 110 },
        { headerName: "IGST", field: "igst", valueFormatter: (p) => this.formatINR(p.value), minWidth: 110 },
        { headerName: "Invoice Value", field: "totalAmount", valueFormatter: (p) => this.formatINR(p.value), minWidth: 140 },
      ],

      b2cLargeColumns: [
        { headerName: "Customer", field: "customer.name", minWidth: 150 },
        { headerName: "Invoice #", field: "invoiceNumber", minWidth: 110 },
        { headerName: "Date", field: "invoiceDate", valueFormatter: (p) => this.formatDate(p.value), minWidth: 110 },
        { headerName: "Place of Supply", field: "placeOfSupply", minWidth: 150 },
        { headerName: "Taxable Value", field: "subtotal", valueFormatter: (p) => this.formatINR(p.value), minWidth: 140 },
        { headerName: "CGST", field: "cgst", valueFormatter: (p) => this.formatINR(p.value), minWidth: 110 },
        { headerName: "SGST", field: "sgst", valueFormatter: (p) => this.formatINR(p.value), minWidth: 110 },
        { headerName: "IGST", field: "igst", valueFormatter: (p) => this.formatINR(p.value), minWidth: 110 },
        { headerName: "Invoice Value", field: "totalAmount", valueFormatter: (p) => this.formatINR(p.value), minWidth: 140 },
      ],

      b2cSmallColumns: [
        { headerName: "State", field: "state", minWidth: 150 },
        { headerName: "Rate (%)", field: "rate", minWidth: 100 },
        { headerName: "Taxable Value", field: "taxableValue", valueFormatter: (p) => this.formatINR(p.value), minWidth: 140 },
        { headerName: "CGST", field: "cgst", valueFormatter: (p) => this.formatINR(p.value), minWidth: 110 },
        { headerName: "SGST", field: "sgst", valueFormatter: (p) => this.formatINR(p.value), minWidth: 110 },
        { headerName: "IGST", field: "igst", valueFormatter: (p) => this.formatINR(p.value), minWidth: 110 },
        { headerName: "Invoice Value", field: "invoiceValue", valueFormatter: (p) => this.formatINR(p.value), minWidth: 140 },
      ],

      hsnColumns: [
        { headerName: "HSN/SAC", field: "hsn", minWidth: 110 },
        { headerName: "Description", field: "description", minWidth: 160 },
        { headerName: "UQC", field: "uqc", minWidth: 90 },
        { headerName: "Qty", field: "totalQty", minWidth: 90 },
        { headerName: "Rate (%)", field: "rate", minWidth: 100 },
        { headerName: "Value", field: "totalValue", valueFormatter: (p) => this.formatINR(p.value), minWidth: 130 },
        { headerName: "CGST", field: "cgst", valueFormatter: (p) => this.formatINR(p.value), minWidth: 110 },
        { headerName: "SGST", field: "sgst", valueFormatter: (p) => this.formatINR(p.value), minWidth: 110 },
        { headerName: "IGST", field: "igst", valueFormatter: (p) => this.formatINR(p.value), minWidth: 110 },
        { headerName: "Total Tax", field: "totalTax", valueFormatter: (p) => this.formatINR(p.value), minWidth: 120 },
      ],

      itcHsnColumns: [
        { headerName: "HSN/SAC", field: "hsn", minWidth: 110 },
        { headerName: "Description", field: "description", minWidth: 160 },
        { headerName: "ITC Eligible", field: "isItcEligible", valueFormatter: (p) => (p.value ? "Yes" : "No"), minWidth: 110 },
        { headerName: "Qty", field: "totalQty", minWidth: 90 },
        { headerName: "Rate (%)", field: "rate", minWidth: 100 },
        { headerName: "Value", field: "totalValue", valueFormatter: (p) => this.formatINR(p.value), minWidth: 130 },
        { headerName: "CGST", field: "cgst", valueFormatter: (p) => this.formatINR(p.value), minWidth: 110 },
        { headerName: "SGST", field: "sgst", valueFormatter: (p) => this.formatINR(p.value), minWidth: 110 },
        { headerName: "IGST", field: "igst", valueFormatter: (p) => this.formatINR(p.value), minWidth: 110 },
        { headerName: "Total Tax", field: "totalTax", valueFormatter: (p) => this.formatINR(p.value), minWidth: 120 },
      ],

      cdnrColumns: [
        { headerName: "GSTIN", field: "customer.gstin", minWidth: 160 },
        { headerName: "Customer", field: "customer.name", minWidth: 150 },
        { headerName: "Credit Note #", field: "creditNoteNumber", minWidth: 130 },
        { headerName: "Date", field: "creditNoteDate", valueFormatter: (p) => this.formatDate(p.value), minWidth: 110 },
        { headerName: "Against Invoice #", field: "invoiceNumber", minWidth: 140 },
        { headerName: "Taxable Value", field: "subtotal", valueFormatter: (p) => this.formatINR(p.value), minWidth: 140 },
        { headerName: "CGST", field: "cgst", valueFormatter: (p) => this.formatINR(p.value), minWidth: 110 },
        { headerName: "SGST", field: "sgst", valueFormatter: (p) => this.formatINR(p.value), minWidth: 110 },
        { headerName: "IGST", field: "igst", valueFormatter: (p) => this.formatINR(p.value), minWidth: 110 },
        { headerName: "Note Value", field: "totalAmount", valueFormatter: (p) => this.formatINR(p.value), minWidth: 140 },
      ],

      cdnurColumns: [
        { headerName: "State", field: "state", minWidth: 150 },
        { headerName: "Rate (%)", field: "rate", minWidth: 100 },
        { headerName: "Taxable Value", field: "taxableValue", valueFormatter: (p) => this.formatINR(p.value), minWidth: 140 },
        { headerName: "CGST", field: "cgst", valueFormatter: (p) => this.formatINR(p.value), minWidth: 110 },
        { headerName: "SGST", field: "sgst", valueFormatter: (p) => this.formatINR(p.value), minWidth: 110 },
        { headerName: "IGST", field: "igst", valueFormatter: (p) => this.formatINR(p.value), minWidth: 110 },
        { headerName: "Note Value", field: "noteValue", valueFormatter: (p) => this.formatINR(p.value), minWidth: 140 },
      ],
    };
  },

  computed: {
    summary() {
      return this.$store.state.gstReports.summary;
    },

    itcSummary() {
      return this.$store.state.gstReports.itcSummary;
    },

    gstr3b() {
      return this.$store.state.gstReports.gstr3b;
    },

    formattedMonth() {
      if (!this.filters.month) return "";
      const d = new Date(this.filters.month);
      return d.toLocaleString("default", { month: "long", year: "numeric" });
    },

    invoiceMobileCard() {
      return {
        title: (row) => row.customer?.name || "-",
        subtitle: (row) => row.invoiceNumber || "-",
        fields: (row) => [
          { label: "Invoice Value", value: this.formatINR(row.totalAmount) },
        ],
      };
    },

    stateRateMobileCard() {
      return {
        title: (row) => row.state || "-",
        subtitle: (row) => `${row.rate}%`,
        fields: (row) => [
          { label: "Invoice Value", value: this.formatINR(row.invoiceValue) },
        ],
      };
    },

    hsnMobileCard() {
      return {
        title: (row) => row.hsn || "-",
        subtitle: (row) => row.description || "-",
        fields: (row) => [
          { label: "Total Tax", value: this.formatINR(row.totalTax) },
        ],
      };
    },

    itcHsnMobileCard() {
      return {
        title: (row) => row.hsn || "-",
        subtitle: (row) => row.description || "-",
        fields: (row) => [
          { label: "ITC Eligible", value: row.isItcEligible ? "Yes" : "No" },
          { label: "Total Tax", value: this.formatINR(row.totalTax) },
        ],
      };
    },

    cdnrMobileCard() {
      return {
        title: (row) => row.customer?.name || "-",
        subtitle: (row) => row.creditNoteNumber || "-",
        fields: (row) => [
          { label: "Note Value", value: this.formatINR(row.totalAmount) },
        ],
      };
    },

    cdnurMobileCard() {
      return {
        title: (row) => row.state || "-",
        subtitle: (row) => `${row.rate}%`,
        fields: (row) => [
          { label: "Note Value", value: this.formatINR(row.noteValue) },
        ],
      };
    },
  },

  created() {
    this.setDefaultMonth();
    this.loadSummary();
  },

  methods: {
    defaultFyStartYear() {
      const now = new Date();
      // Indian FY runs Apr-Mar — before April, "this FY" started last calendar year.
      return now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
    },

    setDefaultMonth() {
      const today = new Date();
      this.filters.month = today.toISOString().slice(0, 7);
    },

    formatINR(value) {
      if (value === null || value === undefined) return "0";
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }).format(value);
    },

    // jsPDF's built-in fonts don't embed the ₹ glyph — Intl's "currency"
    // formatter renders it as a stray "1" in the generated PDF. Plain
    // comma-formatted numbers instead, with a single "(Amounts in Rs.)"
    // caption per table (see addSection in downloadPDF) rather than
    // repeating "Rs." on every cell, which was wrapping/eating column width.
    // Used only inside downloadPDF(); the on-screen grid/Excel export keep
    // formatINR.
    formatPDFCurrency(value) {
      if (value === null || value === undefined) return "0.00";
      return new Intl.NumberFormat("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    },

    formatDate(value) {
      if (!value) return "-";
      const d = new Date(value);
      return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
    },

    buildParams() {
      if (this.filters.type === "month") {
        return { month: this.filters.month };
      }

      // Quarter -> concrete from/to dates, since the backend only
      // understands month/year/from-to, not FY-quarter semantics itself.
      const q = FY_QUARTERS[this.filters.quarter];
      const fyStart = Number(this.filters.fyStartYear);
      const year = this.filters.quarter === "Q4" ? fyStart + 1 : fyStart;

      const from = new Date(year, q.startMonth, 1);
      const to = new Date(year, q.endMonth + 1, 0); // last day of endMonth

      return {
        from: from.toISOString().slice(0, 10),
        to: to.toISOString().slice(0, 10),
      };
    },

    periodLabel() {
      if (this.filters.type === "month") return this.formattedMonth;
      const q = FY_QUARTERS[this.filters.quarter];
      const year = this.filters.quarter === "Q4" ? Number(this.filters.fyStartYear) + 1 : this.filters.fyStartYear;
      return `${q.label} FY${this.filters.fyStartYear}-${String(Number(this.filters.fyStartYear) + 1).slice(-2)} (${year})`;
    },

    periodSuffix() {
      if (this.filters.type === "month") return this.formattedMonth.replace(/\s+/g, "-");
      return `FY${this.filters.fyStartYear}-${this.filters.quarter}`;
    },

    async loadSummary() {
      this.loading = true;
      try {
        const params = this.buildParams();
        // GSTR-3B is inherently a monthly-return concept — filingPeriod
        // (the VendorReturn 2A/2B import key) is only meaningful when the
        // selected period IS a single calendar month. A quarter selection
        // still generates a GSTR-3B summary, just without 2A/2B
        // reconciliation (falls back to books-only ITC).
        const gstr3bParams = {
          ...params,
          filingPeriod: this.filters.type === "month" ? this.filters.month : undefined,
        };
        await Promise.all([
          this.$store.dispatch("gstReports/getGstSummary", params),
          this.$store.dispatch("gstReports/getItcSummary", params),
          this.$store.dispatch("gstReports/getGstr3bSummary", gstr3bParams),
        ]);
      } catch (err) {
        console.error(err);
        this.$toast?.error(
          err?.response?.data?.message || "Failed to load GST report",
        );
      } finally {
        this.loading = false;
      }
    },

    async downloadInvoicesZip() {
      this.downloadingInvoices = true;

      // Captured up front, before the request goes out, so a filter change
      // made while the download is in flight can't retroactively change the
      // name of a file whose contents were already fetched with the old
      // filters.
      const params = this.buildParams();
      const suffix = this.periodSuffix();

      try {
        const response = await this.$store.dispatch(
          "gstReports/downloadInvoicesZip",
          params,
        );

        triggerBlobDownload(
          response.data,
          `invoices-${suffix}.zip`,
          "application/zip",
        );

        this.$toast?.success("Invoices downloaded successfully");
      } catch (err) {
        // With responseType: "blob", an error JSON body still arrives as a
        // Blob — read it back out to surface the server's actual message
        // (e.g. "No invoices found for the selected filters") instead of a
        // generic failure.
        const data = err?.response?.data;
        let message = "Failed to download invoices";

        if (data instanceof Blob) {
          try {
            message = JSON.parse(await data.text())?.message || message;
          } catch (parseErr) {
            // leave default message
          }
        }

        console.error(err);
        this.$toast?.error(message);
      } finally {
        this.downloadingInvoices = false;
      }
    },

    downloadExcel() {
      const s = this.summary;

      const b2bRows = s.b2b.map((r) => [
        r.customer?.gstin || "-",
        r.customer?.name || "-",
        r.invoiceNumber,
        this.formatDate(r.invoiceDate),
        r.placeOfSupply || "-",
        Number(r.subtotal) || 0,
        Number(r.cgst) || 0,
        Number(r.sgst) || 0,
        Number(r.igst) || 0,
        Number(r.totalAmount) || 0,
      ]);

      const b2cLargeRows = s.b2cLarge.map((r) => [
        r.customer?.name || "-",
        r.invoiceNumber,
        this.formatDate(r.invoiceDate),
        r.placeOfSupply || "-",
        Number(r.subtotal) || 0,
        Number(r.cgst) || 0,
        Number(r.sgst) || 0,
        Number(r.igst) || 0,
        Number(r.totalAmount) || 0,
      ]);

      const b2cSmallRows = s.b2cSmall.map((r) => [
        r.state || "-",
        r.rate,
        Number(r.taxableValue) || 0,
        Number(r.cgst) || 0,
        Number(r.sgst) || 0,
        Number(r.igst) || 0,
        Number(r.invoiceValue) || 0,
      ]);

      const docSummaryRows = [
        ["First Invoice #", s.documentSummary.firstInvoiceNumber || "-"],
        ["Last Invoice #", s.documentSummary.lastInvoiceNumber || "-"],
        ["Total Issued", s.documentSummary.totalIssued],
        ["Total Cancelled", s.documentSummary.totalCancelled],
        ["Net", s.documentSummary.net],
      ];

      const hsnRows = s.hsnSummary.map((r) => [
        r.hsn || "-",
        r.description || "-",
        r.uqc || "NOS",
        Number(r.totalQty) || 0,
        r.rate,
        Number(r.totalValue) || 0,
        Number(r.cgst) || 0,
        Number(r.sgst) || 0,
        Number(r.igst) || 0,
        Number(r.totalTax) || 0,
      ]);

      const liabilityRows = [
        ["Taxable Value", Number(s.outwardTaxLiability.taxableValue) || 0],
        ["CGST", Number(s.outwardTaxLiability.cgst) || 0],
        ["SGST", Number(s.outwardTaxLiability.sgst) || 0],
        ["IGST", Number(s.outwardTaxLiability.igst) || 0],
        ["Total Liability", Number(s.outwardTaxLiability.total) || 0],
      ];

      const cdnrRows = (s.cdnr || []).map((r) => [
        r.customer?.gstin || "-",
        r.customer?.name || "-",
        r.creditNoteNumber,
        this.formatDate(r.creditNoteDate),
        r.invoiceNumber || "-",
        Number(r.subtotal) || 0,
        Number(r.cgst) || 0,
        Number(r.sgst) || 0,
        Number(r.igst) || 0,
        Number(r.totalAmount) || 0,
      ]);

      const cdnurRows = (s.cdnur || []).map((r) => [
        r.state || "-",
        r.rate,
        Number(r.taxableValue) || 0,
        Number(r.cgst) || 0,
        Number(r.sgst) || 0,
        Number(r.igst) || 0,
        Number(r.noteValue) || 0,
      ]);

      downloadMultiSheetWorkbook(
        [
          {
            name: "B2B Invoices",
            title: "B2B Invoices",
            subtitle: this.periodLabel(),
            headers: ["GSTIN", "Customer", "Invoice #", "Date", "Place of Supply", "Taxable Value", "CGST", "SGST", "IGST", "Invoice Value"],
            rows: b2bRows,
            columnWidths: [18, 20, 14, 12, 18, 14, 12, 12, 12, 14],
            numberColumns: [5, 6, 7, 8, 9],
          },
          {
            name: "B2C Large",
            title: "B2C Large Invoices",
            subtitle: this.periodLabel(),
            headers: ["Customer", "Invoice #", "Date", "Place of Supply", "Taxable Value", "CGST", "SGST", "IGST", "Invoice Value"],
            rows: b2cLargeRows,
            columnWidths: [20, 14, 12, 18, 14, 12, 12, 12, 14],
            numberColumns: [4, 5, 6, 7, 8],
          },
          {
            name: "B2C Small Summary",
            title: "B2C Small Summary",
            subtitle: this.periodLabel(),
            headers: ["State", "Rate (%)", "Taxable Value", "CGST", "SGST", "IGST", "Invoice Value"],
            rows: b2cSmallRows,
            columnWidths: [18, 10, 14, 12, 12, 12, 14],
            numberColumns: [2, 3, 4, 5, 6],
          },
          {
            name: "Document Summary",
            title: "Document Summary",
            subtitle: this.periodLabel(),
            headers: ["Metric", "Value"],
            rows: docSummaryRows,
            columnWidths: [20, 20],
          },
          {
            name: "HSN Summary",
            title: "HSN-wise Summary",
            subtitle: this.periodLabel(),
            headers: ["HSN/SAC", "Description", "UQC", "Qty", "Rate (%)", "Value", "CGST", "SGST", "IGST", "Total Tax"],
            rows: hsnRows,
            columnWidths: [12, 24, 8, 10, 10, 14, 12, 12, 12, 14],
            numberColumns: [5, 6, 7, 8, 9],
          },
          {
            name: "Tax Liability",
            title: "Outward Tax Liability Summary",
            subtitle: this.periodLabel(),
            headers: ["Metric", "Amount"],
            rows: liabilityRows,
            columnWidths: [20, 20],
            numberColumns: [1],
          },
          {
            name: "CDNR",
            title: "Credit Notes (Registered) — CDNR",
            subtitle: this.periodLabel(),
            headers: ["GSTIN", "Customer", "Credit Note #", "Date", "Against Invoice #", "Taxable Value", "CGST", "SGST", "IGST", "Note Value"],
            rows: cdnrRows,
            columnWidths: [18, 20, 14, 12, 16, 14, 12, 12, 12, 14],
            numberColumns: [5, 6, 7, 8, 9],
          },
          {
            name: "CDNUR",
            title: "Credit Notes (Unregistered) — CDNUR",
            subtitle: this.periodLabel(),
            headers: ["State", "Rate (%)", "Taxable Value", "CGST", "SGST", "IGST", "Note Value"],
            rows: cdnurRows,
            columnWidths: [18, 10, 14, 12, 12, 12, 14],
            numberColumns: [2, 3, 4, 5, 6],
          },
        ],
        `gstr1-${this.periodSuffix()}.xlsx`,
      );
    },

    downloadPDF() {
      const s = this.summary;
      const doc = new jsPDF();
      const M = 14;

      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("GST Report (GSTR-1)", M, 15);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(this.periodLabel(), M, 22);

      let startY = 30;

      const addSection = (title, head, body, { hasMoney = false } = {}) => {
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text(title, M, startY);

        if (hasMoney) {
          doc.setFontSize(8);
          doc.setFont("helvetica", "italic");
          doc.setTextColor(120, 130, 145);
          doc.text("(Amounts in Rs.)", M, startY + 5);
          doc.setTextColor(0, 0, 0);
        }

        const tableStartOffset = hasMoney ? 9 : 4;

        if (!body.length) {
          doc.setFontSize(9);
          doc.setFont("helvetica", "normal");
          doc.text("No records for this period.", M, startY + tableStartOffset + 2);
          startY += tableStartOffset + 10;
          return;
        }

        autoTable(doc, {
          startY: startY + tableStartOffset,
          head: [head],
          body,
          styles: { fontSize: 7.5, cellPadding: 2 },
          headStyles: { fillColor: [10, 90, 144], textColor: [255, 255, 255] },
          margin: { left: M, right: M },
        });

        startY = doc.lastAutoTable.finalY + 12;
        if (startY > doc.internal.pageSize.getHeight() - 30) {
          doc.addPage();
          startY = 20;
        }
      };

      addSection(
        "B2B Invoices",
        ["GSTIN", "Customer", "Invoice #", "Date", "POS", "Taxable Value", "CGST", "SGST", "IGST", "Invoice Value"],
        s.b2b.map((r) => [
          r.customer?.gstin || "-", r.customer?.name || "-", r.invoiceNumber,
          this.formatDate(r.invoiceDate), r.placeOfSupply || "-",
          this.formatPDFCurrency(r.subtotal), this.formatPDFCurrency(r.cgst), this.formatPDFCurrency(r.sgst),
          this.formatPDFCurrency(r.igst), this.formatPDFCurrency(r.totalAmount),
        ]),
        { hasMoney: true },
      );

      addSection(
        "B2C Large Invoices",
        ["Customer", "Invoice #", "Date", "POS", "Taxable Value", "CGST", "SGST", "IGST", "Invoice Value"],
        s.b2cLarge.map((r) => [
          r.customer?.name || "-", r.invoiceNumber, this.formatDate(r.invoiceDate),
          r.placeOfSupply || "-", this.formatPDFCurrency(r.subtotal), this.formatPDFCurrency(r.cgst),
          this.formatPDFCurrency(r.sgst), this.formatPDFCurrency(r.igst), this.formatPDFCurrency(r.totalAmount),
        ]),
        { hasMoney: true },
      );

      addSection(
        "B2C Small Summary",
        ["State", "Rate (%)", "Taxable Value", "CGST", "SGST", "IGST", "Invoice Value"],
        s.b2cSmall.map((r) => [
          r.state || "-", r.rate, this.formatPDFCurrency(r.taxableValue), this.formatPDFCurrency(r.cgst),
          this.formatPDFCurrency(r.sgst), this.formatPDFCurrency(r.igst), this.formatPDFCurrency(r.invoiceValue),
        ]),
        { hasMoney: true },
      );

      addSection(
        "Document Summary",
        ["Metric", "Value"],
        [
          ["Invoice Range", `${s.documentSummary.firstInvoiceNumber || "-"} – ${s.documentSummary.lastInvoiceNumber || "-"}`],
          ["Total Issued", String(s.documentSummary.totalIssued)],
          ["Total Cancelled", String(s.documentSummary.totalCancelled)],
          ["Net", String(s.documentSummary.net)],
        ],
      );

      addSection(
        "HSN-wise Summary",
        ["HSN/SAC", "Description", "UQC", "Qty", "Rate (%)", "Value", "CGST", "SGST", "IGST", "Total Tax"],
        s.hsnSummary.map((r) => [
          r.hsn || "-", r.description || "-", r.uqc || "NOS", r.totalQty, r.rate,
          this.formatPDFCurrency(r.totalValue), this.formatPDFCurrency(r.cgst), this.formatPDFCurrency(r.sgst),
          this.formatPDFCurrency(r.igst), this.formatPDFCurrency(r.totalTax),
        ]),
        { hasMoney: true },
      );

      addSection(
        "Outward Tax Liability Summary",
        ["Metric", "Amount"],
        [
          ["Taxable Value", this.formatPDFCurrency(s.outwardTaxLiability.taxableValue)],
          ["CGST", this.formatPDFCurrency(s.outwardTaxLiability.cgst)],
          ["SGST", this.formatPDFCurrency(s.outwardTaxLiability.sgst)],
          ["IGST", this.formatPDFCurrency(s.outwardTaxLiability.igst)],
          ["Total Liability", this.formatPDFCurrency(s.outwardTaxLiability.total)],
        ],
        { hasMoney: true },
      );

      addSection(
        "Credit Notes (Registered) — CDNR",
        ["GSTIN", "Customer", "Credit Note #", "Date", "Against Invoice #", "Taxable Value", "CGST", "SGST", "IGST", "Note Value"],
        (s.cdnr || []).map((r) => [
          r.customer?.gstin || "-", r.customer?.name || "-", r.creditNoteNumber,
          this.formatDate(r.creditNoteDate), r.invoiceNumber || "-",
          this.formatPDFCurrency(r.subtotal), this.formatPDFCurrency(r.cgst), this.formatPDFCurrency(r.sgst),
          this.formatPDFCurrency(r.igst), this.formatPDFCurrency(r.totalAmount),
        ]),
        { hasMoney: true },
      );

      addSection(
        "Credit Notes (Unregistered) — CDNUR",
        ["State", "Rate (%)", "Taxable Value", "CGST", "SGST", "IGST", "Note Value"],
        (s.cdnur || []).map((r) => [
          r.state || "-", r.rate, this.formatPDFCurrency(r.taxableValue), this.formatPDFCurrency(r.cgst),
          this.formatPDFCurrency(r.sgst), this.formatPDFCurrency(r.igst), this.formatPDFCurrency(r.noteValue),
        ]),
        { hasMoney: true },
      );

      doc.save(`gstr1-${this.periodSuffix()}.pdf`);
    },

    downloadItcExcel() {
      const s = this.itcSummary;

      const hsnRows = s.hsnSummary.map((r) => [
        r.hsn || "-",
        r.description || "-",
        r.isItcEligible ? "Yes" : "No",
        Number(r.totalQty) || 0,
        r.rate,
        Number(r.totalValue) || 0,
        Number(r.cgst) || 0,
        Number(r.sgst) || 0,
        Number(r.igst) || 0,
        Number(r.totalTax) || 0,
      ]);

      const summaryRows = [
        ["ITC Eligible CGST", Number(s.itcEligible.cgst) || 0],
        ["ITC Eligible SGST", Number(s.itcEligible.sgst) || 0],
        ["ITC Eligible IGST", Number(s.itcEligible.igst) || 0],
        ["Total ITC Eligible", Number(s.itcEligible.total) || 0],
        ["ITC Blocked CGST", Number(s.itcBlocked.cgst) || 0],
        ["ITC Blocked SGST", Number(s.itcBlocked.sgst) || 0],
        ["ITC Blocked IGST", Number(s.itcBlocked.igst) || 0],
        ["Total ITC Blocked", Number(s.itcBlocked.total) || 0],
        ["ITC Reversed CGST (Debit Notes)", Number(s.itcReversed.cgst) || 0],
        ["ITC Reversed SGST (Debit Notes)", Number(s.itcReversed.sgst) || 0],
        ["ITC Reversed IGST (Debit Notes)", Number(s.itcReversed.igst) || 0],
        ["Total ITC Reversed", Number(s.itcReversed.total) || 0],
        ["Net ITC Eligible", Number(s.netItcEligible.total) || 0],
        ["Total Purchase Value", Number(s.totalPurchaseValue) || 0],
        ["Total Taxable Value", Number(s.totalTaxableValue) || 0],
      ];

      downloadMultiSheetWorkbook(
        [
          {
            name: "ITC Summary",
            title: "ITC Summary",
            subtitle: this.periodLabel(),
            headers: ["Metric", "Amount"],
            rows: summaryRows,
            columnWidths: [24, 20],
            numberColumns: [1],
          },
          {
            name: "HSN-wise ITC",
            title: "HSN-wise ITC Summary",
            subtitle: this.periodLabel(),
            headers: ["HSN/SAC", "Description", "ITC Eligible", "Qty", "Rate (%)", "Value", "CGST", "SGST", "IGST", "Total Tax"],
            rows: hsnRows,
            columnWidths: [12, 24, 12, 10, 10, 14, 12, 12, 12, 14],
            numberColumns: [5, 6, 7, 8, 9],
          },
        ],
        `itc-summary-${this.periodSuffix()}.xlsx`,
      );
    },

    downloadItcPDF() {
      const s = this.itcSummary;
      const doc = new jsPDF();
      const M = 14;

      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("ITC Summary", M, 15);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(this.periodLabel(), M, 22);

      let startY = 30;

      const addSection = (title, head, body, { hasMoney = false } = {}) => {
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text(title, M, startY);

        if (hasMoney) {
          doc.setFontSize(8);
          doc.setFont("helvetica", "italic");
          doc.setTextColor(120, 130, 145);
          doc.text("(Amounts in Rs.)", M, startY + 5);
          doc.setTextColor(0, 0, 0);
        }

        const tableStartOffset = hasMoney ? 9 : 4;

        if (!body.length) {
          doc.setFontSize(9);
          doc.setFont("helvetica", "normal");
          doc.text("No records for this period.", M, startY + tableStartOffset + 2);
          startY += tableStartOffset + 10;
          return;
        }

        autoTable(doc, {
          startY: startY + tableStartOffset,
          head: [head],
          body,
          styles: { fontSize: 7.5, cellPadding: 2 },
          headStyles: { fillColor: [10, 90, 144], textColor: [255, 255, 255] },
          margin: { left: M, right: M },
        });

        startY = doc.lastAutoTable.finalY + 12;
        if (startY > doc.internal.pageSize.getHeight() - 30) {
          doc.addPage();
          startY = 20;
        }
      };

      addSection(
        "ITC Summary",
        ["Metric", "Amount"],
        [
          ["ITC Eligible CGST", this.formatPDFCurrency(s.itcEligible.cgst)],
          ["ITC Eligible SGST", this.formatPDFCurrency(s.itcEligible.sgst)],
          ["ITC Eligible IGST", this.formatPDFCurrency(s.itcEligible.igst)],
          ["Total ITC Eligible", this.formatPDFCurrency(s.itcEligible.total)],
          ["ITC Blocked CGST", this.formatPDFCurrency(s.itcBlocked.cgst)],
          ["ITC Blocked SGST", this.formatPDFCurrency(s.itcBlocked.sgst)],
          ["ITC Blocked IGST", this.formatPDFCurrency(s.itcBlocked.igst)],
          ["Total ITC Blocked", this.formatPDFCurrency(s.itcBlocked.total)],
          ["ITC Reversed (Debit Notes)", this.formatPDFCurrency(s.itcReversed.total)],
          ["Net ITC Eligible", this.formatPDFCurrency(s.netItcEligible.total)],
        ],
        { hasMoney: true },
      );

      addSection(
        "HSN-wise ITC Summary",
        ["HSN/SAC", "Description", "ITC Eligible", "Qty", "Rate (%)", "Value", "CGST", "SGST", "IGST", "Total Tax"],
        s.hsnSummary.map((r) => [
          r.hsn || "-", r.description || "-", r.isItcEligible ? "Yes" : "No", r.totalQty, r.rate,
          this.formatPDFCurrency(r.totalValue), this.formatPDFCurrency(r.cgst), this.formatPDFCurrency(r.sgst),
          this.formatPDFCurrency(r.igst), this.formatPDFCurrency(r.totalTax),
        ]),
        { hasMoney: true },
      );

      doc.save(`itc-summary-${this.periodSuffix()}.pdf`);
    },
  },
};
</script>

<style scoped>
.gst-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.gst-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
}

.gst-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.gst-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.gst-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.gst-disclaimer {
  margin-bottom: 20px;
  font-size: 13px;
}

.gst-tabs {
  margin-bottom: 20px;
}

.gst-tab-content {
  overflow: visible;
}

.gst-filter-bar {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 16px 16px 4px;
  margin-bottom: 24px;
}

.gst-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 0;
  padding-top: 4px;
}

.gst-apply-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.gst-kpi-card {
  border-radius: 0;
  color: #fff;
  box-shadow: 0 6px 16px rgba(15, 30, 50, 0.12);
}

.gst-kpi-card--value {
  background: linear-gradient(135deg, #052f5e 0%, #0a5a90 100%);
}
.gst-kpi-card--tax {
  background: linear-gradient(135deg, #7a1f1f 0%, #b23a3a 100%);
}
.gst-kpi-card--total {
  background: linear-gradient(135deg, #0d8478 0%, #15a355 100%);
}

.gst-kpi-label {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
}

.gst-kpi-value {
  font-size: 20px;
  font-weight: 700;
  margin-top: 6px;
  letter-spacing: -0.01em;
}

.gst-section-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  margin-bottom: 10px;
}

.gst-doc-stat {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 12px 14px;
}

.gst-doc-label {
  font-size: 11px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.gst-doc-value {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin-top: 4px;
}

.gst-empty-state {
  text-align: center;
  padding: 48px 0;
  color: #94a3b8;
  font-size: 14px;
}

.gst-table {
  border: 1px solid #eef1f5;
  border-radius: 0;
}

.gst-total-row {
  font-weight: 700;
  background: #f8fafc;
}

.v-menu__content {
  z-index: 9999 !important;
}
</style>
